import { useGridRootProps } from "@components/DataGrid/hooks/useGridRootProps";
import { Box } from "@mui/material";
import { unstable_composeClasses as composeClasses } from "@mui/utils";
import { GridGroupNode, GridRenderCellParams, getDataGridUtilityClass, gridFilteredDescendantCountLookupSelector, useGridSelector } from "@mui/x-data-grid";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { DataGridPremiumProcessedProps } from "@mui/x-data-grid-premium/models/dataGridPremiumProps";
import { MouseEvent } from "react";

type OwnerState = DataGridPremiumProcessedProps;

export const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ["treeDataGroupingCell"],
    toggle: ["treeDataGroupingCellToggle"],
  };

  return composeClasses(slots, getDataGridUtilityClass, classes);
};

export interface GridTreeDataGroupingCellProps extends GridRenderCellParams<any, any, any, GridGroupNode> {
  hideDescendantCount?: boolean;
  /**
   * The cell offset multiplier used for calculating cell offset (`rowNode.depth * offsetMultiplier` px).
   * @default 2
   */
  offsetMultiplier?: number;
}

export const CustomGridTreeDataGroupingCell = (props: GridTreeDataGroupingCellProps) => {
  const { id, field, formattedValue, rowNode, hideDescendantCount, offsetMultiplier = 2 } = props;

  const rootProps = useGridRootProps();
  const apiRef = useGridApiContext();
  const classes = useUtilityClasses(rootProps);
  const filteredDescendantCountLookup = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector);

  const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;

  const Icon = rowNode.childrenExpanded ? rootProps.slots.treeDataCollapseIcon : rootProps.slots.treeDataExpandIcon;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  //console.log('formattedValue: ', formattedValue, props);
  return (
    <Box className={classes.root} sx={{ ml: rowNode.depth * offsetMultiplier }}>
      <div className={classes.toggle}>
        {filteredDescendantCount > 0 && (
          <rootProps.slots.baseIconButton
            size="small"
            onClick={handleClick}
            tabIndex={-1}
            aria-label={rowNode.childrenExpanded ? apiRef.current.getLocaleText("treeDataCollapse") : apiRef.current.getLocaleText("treeDataExpand")}
            {...rootProps?.slotProps?.baseIconButton}
          >
            <Icon fontSize="inherit" />
          </rootProps.slots.baseIconButton>
        )}
      </div>
      <span>
        {formattedValue === undefined ? rowNode.groupingKey : formattedValue}
        {!hideDescendantCount && filteredDescendantCount > 0 ? ` (${filteredDescendantCount})` : ""}
      </span>
    </Box>
  );
};
